import { Button } from '@cretesuite/design-system'
import {
  IonFooter,
  IonIcon,
  IonPage,
  IonSpinner,
  useIonActionSheet,
} from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { cameraOutline } from 'ionicons/icons'
import { Redirect, useParams } from 'react-router-dom'
import { useQuery } from 'urql'
import { OrderPhotosSection } from '../components/order/OrderPhotosSection'
import { usePhotoUpload } from '../context/PhotoUploadProvider'
import { GetOrderDetail } from '../graphql/orders'
import { type PhotoSource, capturePhoto } from '../helpers/photoCapture'
import { getIsOnline } from '../helpers/networkStatus'
import { useFeature } from '../hooks/useFeatures'
import { useOrderPhotoUpload } from '../hooks/useOrderPhotoUpload'
import { useOrderPhotos } from '../hooks/useOrderPhotos'
import { useToast } from '../hooks/useToast'
import { StandardLayout } from '../layouts/StandardLayout'

export const OrderPhotosPage = () => {
  const { _ } = useLingui()
  const { assignmentId, orderId } = useParams<{
    assignmentId?: string
    orderId: string
  }>()

  const orderIdNum = Number.parseInt(orderId, 10)
  const orderPhotosEnabled = useFeature('orderPhotosEnabled')
  const [presentActionSheet] = useIonActionSheet()
  const [presentToast] = useToast()

  // Title only — cache-first hits the entry already normalized by the
  // order detail page the crew navigated from.
  const [detailResult] = useQuery({
    query: GetOrderDetail,
    variables: { orderId: orderIdNum },
    requestPolicy: 'cache-first',
    pause: !orderPhotosEnabled,
  })
  const order = detailResult.data?.order

  // Server photos ∪ locally queued photos, with pending/failed flags
  const { photos, fetching, error, refetch } = useOrderPhotos(orderIdNum, {
    pause: !orderPhotosEnabled,
  })

  const { addPhoto, uploading } = useOrderPhotoUpload(orderIdNum)
  const { discardPhoto } = usePhotoUpload()

  const captureAndUpload = async (source: PhotoSource) => {
    // Capture and upload fail differently: a denied permission must not be
    // reported as a network problem the crew is told to retry.
    let captured: Awaited<ReturnType<typeof capturePhoto>>
    try {
      captured = await capturePhoto(source)
    } catch (error) {
      console.error('[OrderPhotosPage] photo capture failed', error)
      // No camera hardware (e.g. Simulator) is not a permissions problem —
      // don't send the crew to Settings for it
      const cameraUnavailable =
        error instanceof Error && /not available|simulator/i.test(error.message)
      presentToast({
        color: 'danger',
        message: cameraUnavailable
          ? _(msg`The camera is not available on this device.`)
          : _(
              msg`Could not open the camera or photo library. Check the app permissions.`,
            ),
        duration: 3000,
      })
      return
    }
    if (captured === null) return

    try {
      const outcome = await addPhoto(captured.blob, captured.takenAtIso)
      // 'uploaded' refresh is owned by useOrderPhotos (uploadSerial bump)
      if (outcome === 'uploaded') {
        presentToast({
          color: 'success',
          message: _(msg`Photo uploaded.`),
          duration: 2500,
        })
        return
      }
      // Queued: durable on the device. Online it rides the drain already in
      // progress; offline it waits for connectivity.
      presentToast({
        color: 'success',
        message: getIsOnline()
          ? _(msg`Saved. It will upload in a moment.`)
          : _(msg`Saved. It will sync when you're back online.`),
        duration: 3000,
      })
    } catch (error) {
      console.error('[OrderPhotosPage] photo upload failed', error)
      presentToast({
        color: 'danger',
        message: _(msg`Could not upload the photo. Please try again.`),
        duration: 3000,
      })
    }
  }

  const handleAddPhoto = () => {
    presentActionSheet({
      header: _(msg`Add Photo`),
      buttons: [
        {
          text: _(msg`Take Photo`),
          handler: () => {
            void captureAndUpload('camera')
          },
        },
        {
          text: _(msg`Choose from Library`),
          handler: () => {
            void captureAndUpload('library')
          },
        },
        { text: _(msg`Cancel`), role: 'cancel' },
      ],
    })
  }

  const backPath = assignmentId
    ? `/assignments/${assignmentId}/orders/${orderId}`
    : `/orders/${orderId}`

  const title =
    order?.site?.name || order?.customer?.name || order?.name || _(msg`Photos`)

  // Routes are flag-gated too; a stale back-stack / resumed URL lands here,
  // so send the crew back to the order instead of a dead-end shell.
  if (!orderPhotosEnabled) {
    return <Redirect to={backPath} />
  }

  return (
    <IonPage>
      <StandardLayout title={title} defaultBackTo={backPath}>
        <div className='order-photos-page'>
          <OrderPhotosSection
            photos={photos}
            fetching={fetching}
            error={error ? { onRetry: refetch } : undefined}
            onDiscard={discardPhoto}
          />
        </div>
      </StandardLayout>

      {/* Anchored main action — always visible, no scrolling (Brandon) */}
      <IonFooter className='ion-no-border'>
        <div className='order-photos-page__footer'>
          <Button
            className='order-photos-page__add-btn'
            onClick={handleAddPhoto}
            disabled={uploading}
          >
            {uploading ? (
              <IonSpinner name='crescent' />
            ) : (
              <IonIcon icon={cameraOutline} slot='start' />
            )}
            {uploading ? _(msg`Uploading...`) : _(msg`Add Photo`)}
          </Button>
        </div>
      </IonFooter>
    </IonPage>
  )
}

export default OrderPhotosPage
